import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../../contexts/PusherContext';
import api from '../../../api';
import { AgentEvents } from '@dreamai/types';
import './project-app-kanban-block.scss';

interface ColumnDef {
  key: string;
  label: string;
  type?: string;
  options?: string[];
}

const KANBAN_COLORS: Record<string, string> = {
  red: '#FBD0D9',
  green: '#D2F9F4',
  yellow: '#FFF7CC',
  blue: '#D5E9F6',
  brown: '#F7F3ED',
  grey: '#838B9B',
};

const MID_CYCLE = ['yellow', 'blue', 'brown'] as const;

function getDefaultColumnColors(cols: string[]): Record<string, string> {
  const colors: Record<string, string> = {};
  if (cols.length === 0) return colors;
  if (cols.length === 1) {
    colors[cols[0]] = KANBAN_COLORS.red;
    return colors;
  }
  colors[cols[0]] = KANBAN_COLORS.red;
  colors[cols[cols.length - 1]] = KANBAN_COLORS.green;
  for (let i = 1; i < cols.length - 1; i++) {
    colors[cols[i]] = KANBAN_COLORS[MID_CYCLE[(i - 1) % MID_CYCLE.length]];
  }
  return colors;
}

interface ProjectAppKanbanBlockProps {
  blockId: string;
  data: Record<string, unknown>;
  embedded?: boolean;
}

const ProjectAppKanbanBlock: React.FC<ProjectAppKanbanBlockProps> = ({
  blockId,
  data,
  embedded,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const dataTableId = data.dataTableId as string | undefined;
  const groupByColumn = data.groupByColumn as string | undefined;
  const hideUnknownColumn = (data.hideUnknownColumn as boolean) || false;
  const savedColumnColors = (data.columnColors as Record<string, string>) || {};
  const isSavingRef = useRef(false);

  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Display settings (persisted in block data)
  const [titleField, setTitleField] = useState<string | null>(
    (data.titleField as string) || null,
  );
  const [visibleFields, setVisibleFields] = useState<string[]>(
    (data.visibleFields as string[]) || [],
  );
  const [showSettings, setShowSettings] = useState(false);
  const [draftTitleField, setDraftTitleField] = useState<string | null>(null);
  const [draftVisibleFields, setDraftVisibleFields] = useState<string[]>([]);

  // Edit modal state
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(
    null,
  );
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Card drag state
  const dragItem = useRef<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Column drag state
  const dragColumn = useRef<string | null>(null);
  const [dragOverColTarget, setDragOverColTarget] = useState<string | null>(
    null,
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(
    (data.columnOrder as string[]) || [],
  );

  // Fetch data table
  const fetchData = useCallback(async () => {
    if (!dataTableId) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get(`/data-tables/${dataTableId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && res.data.dataTable) {
        const dt = res.data.dataTable;
        const cols: ColumnDef[] = Array.isArray(dt.headings) ? dt.headings : [];
        const vals: Record<string, unknown>[] = Array.isArray(dt.values)
          ? dt.values
          : [];
        setColumns(cols);

        // Normalise keys
        const colKeys = cols.map((c) => c.key);
        const toSnake = (s: string) =>
          s.replace(/([A-Z])/g, '_$1').toLowerCase();
        const normalise = (row: Record<string, unknown>) => {
          const out: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(row)) {
            const snake = toSnake(k);
            if (colKeys.includes(k)) {
              out[k] = v;
            } else if (colKeys.includes(snake)) {
              out[snake] = v;
            } else {
              out[k] = v;
            }
          }
          return out;
        };

        setRows(
          vals.map((r, i) => ({
            ...normalise(r),
            _id: `row-${i}`,
          })),
        );
      }
    } catch (err) {
      console.error('[Kanban] Failed to fetch data table:', err);
    } finally {
      setLoading(false);
    }
  }, [dataTableId, getAccessTokenSilently]);

  useEffect(() => {
    if (!dataTableId) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [dataTableId, fetchData]);

  // Listen for Pusher events when another component updates this data table
  useEffect(() => {
    if (!pusherChannel || !dataTableId) return;
    const handler = (event: { dataTableId: string }) => {
      if (event.dataTableId === dataTableId && !isSavingRef.current) {
        fetchData();
      }
    };
    pusherChannel.bind(AgentEvents.DATA_TABLE_UPDATED, handler);
    return () => {
      pusherChannel.unbind(AgentEvents.DATA_TABLE_UPDATED, handler);
    };
  }, [pusherChannel, dataTableId, fetchData]);

  const persistData = useCallback(
    async (
      updatedColumns: ColumnDef[],
      updatedRows: Record<string, unknown>[],
    ) => {
      if (!dataTableId) return;
      setSaving(true);
      isSavingRef.current = true;
      try {
        const token = await getAccessTokenSilently();
        const cleanRows = updatedRows.map((r) => {
          const { _id, ...rest } = r;
          return rest;
        });
        await api.patch(
          `/data-tables/${dataTableId}`,
          { headings: updatedColumns, values: cleanRows },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('[Kanban] Failed to save:', err);
      } finally {
        setSaving(false);
        setTimeout(() => {
          isSavingRef.current = false;
        }, 500);
      }
    },
    [dataTableId, getAccessTokenSilently],
  );

  // Find the enum column used for grouping
  const enumCol = useMemo(
    () => columns.find((c) => c.key === groupByColumn && c.type === 'enum'),
    [columns, groupByColumn],
  );

  // The list of kanban columns (from enum options), respecting saved order
  const kanbanColumns = useMemo(() => {
    const options = enumCol?.options || [];
    if (columnOrder.length === 0) return options;
    // Use saved order, appending any new options not yet in the order
    const ordered = columnOrder.filter((c) => options.includes(c));
    const remaining = options.filter((c) => !columnOrder.includes(c));
    return [...ordered, ...remaining];
  }, [enumCol, columnOrder]);

  // Non-enum columns (available for cards)
  const cardColumns = useMemo(
    () => columns.filter((c) => c.key !== groupByColumn),
    [columns, groupByColumn],
  );

  // Resolved title column: use saved setting or fall back to first text column
  const resolvedTitleCol = useMemo(
    () =>
      cardColumns.find((c) => c.key === titleField) ||
      cardColumns.find((c) => !c.type || c.type === 'text'),
    [cardColumns, titleField],
  );

  // Resolved visible fields: use saved setting or show first 3 non-title fields
  const resolvedVisibleCols = useMemo(() => {
    const nonTitle = cardColumns.filter((c) => c.key !== resolvedTitleCol?.key);
    if (visibleFields.length > 0) {
      return nonTitle.filter((c) => visibleFields.includes(c.key));
    }
    return nonTitle.slice(0, 3);
  }, [cardColumns, resolvedTitleCol, visibleFields]);

  // Group rows by enum value
  const groupedRows = useMemo(() => {
    const groups: Record<string, Record<string, unknown>[]> = {};
    for (const col of kanbanColumns) {
      groups[col] = [];
    }
    groups['__unknown__'] = [];
    for (const row of rows) {
      const val = (row[groupByColumn || ''] as string) || '';
      if (groups[val]) {
        groups[val].push(row);
      } else {
        groups['__unknown__'].push(row);
      }
    }
    return groups;
  }, [rows, kanbanColumns, groupByColumn]);

  const showUnknown =
    !hideUnknownColumn && (groupedRows['__unknown__']?.length || 0) > 0;

  // Resolve column colours: saved overrides defaults
  const columnColors = useMemo((): Record<string, string> => {
    const defaults = getDefaultColumnColors(kanbanColumns);
    return {
      ...defaults,
      ...savedColumnColors,
      __unknown__: KANBAN_COLORS.grey,
    };
  }, [kanbanColumns, savedColumnColors]);

  // Persist display settings to block data
  const persistBlockSettings = useCallback(
    async (newTitleField: string | null, newVisibleFields: string[]) => {
      if (!blockId) return;
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/project-apps/blocks/${blockId}/data`,
          {
            data: {
              ...data,
              titleField: newTitleField,
              visibleFields: newVisibleFields,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('[Kanban] Failed to save display settings:', err);
      }
    },
    [blockId, data, getAccessTokenSilently],
  );

  const openSettings = useCallback(() => {
    setDraftTitleField(resolvedTitleCol?.key || null);
    setDraftVisibleFields(resolvedVisibleCols.map((c) => c.key));
    setShowSettings(true);
  }, [resolvedTitleCol, resolvedVisibleCols]);

  const handleSaveSettings = useCallback(() => {
    setTitleField(draftTitleField);
    setVisibleFields(draftVisibleFields);
    setShowSettings(false);
    persistBlockSettings(draftTitleField, draftVisibleFields);
  }, [draftTitleField, draftVisibleFields, persistBlockSettings]);

  // Persist column order to block data
  const persistColumnOrder = useCallback(
    async (newOrder: string[]) => {
      if (!blockId) return;
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/project-apps/blocks/${blockId}/data`,
          { data: { ...data, columnOrder: newOrder } },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('[Kanban] Failed to save column order:', err);
      }
    },
    [blockId, data, getAccessTokenSilently],
  );

  // Card drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, rowId: string) => {
    dragItem.current = rowId;
    e.dataTransfer.setData('text/card', rowId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, columnValue: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragItem.current) {
        setDragOverColumn(columnValue);
      }
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetColumn: string) => {
      e.preventDefault();
      setDragOverColumn(null);
      const rowId = dragItem.current;
      if (!rowId || !groupByColumn) return;

      const newValue = targetColumn === '__unknown__' ? '' : targetColumn;
      const updatedRows = rows.map((r) =>
        r._id === rowId ? { ...r, [groupByColumn]: newValue } : r,
      );
      setRows(updatedRows);
      persistData(columns, updatedRows);
      dragItem.current = null;
    },
    [rows, columns, groupByColumn, persistData],
  );

  // Column drag handlers
  const handleColDragStart = useCallback(
    (e: React.DragEvent, colValue: string) => {
      dragColumn.current = colValue;
      e.dataTransfer.setData('text/column', colValue);
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  const handleColDragOver = useCallback(
    (e: React.DragEvent, colValue: string) => {
      if (!dragColumn.current) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverColTarget(colValue);
    },
    [],
  );

  const handleColDragLeave = useCallback(() => {
    setDragOverColTarget(null);
  }, []);

  const handleColDrop = useCallback(
    (e: React.DragEvent, targetCol: string) => {
      e.preventDefault();
      setDragOverColTarget(null);
      const sourceCol = dragColumn.current;
      dragColumn.current = null;
      if (!sourceCol || sourceCol === targetCol) return;

      const current = [...kanbanColumns];
      const fromIdx = current.indexOf(sourceCol);
      const toIdx = current.indexOf(targetCol);
      if (fromIdx === -1 || toIdx === -1) return;

      current.splice(fromIdx, 1);
      current.splice(toIdx, 0, sourceCol);
      setColumnOrder(current);
      persistColumnOrder(current);
    },
    [kanbanColumns, persistColumnOrder],
  );

  const handleColDragEnd = useCallback(() => {
    dragColumn.current = null;
    setDragOverColTarget(null);
  }, []);

  // Edit modal handlers
  const openEditModal = useCallback(
    (row: Record<string, unknown>) => {
      setEditingRow(row);
      const vals: Record<string, string> = {};
      for (const col of columns) {
        vals[col.key] = row[col.key] != null ? String(row[col.key]) : '';
      }
      setEditValues(vals);
    },
    [columns],
  );

  const handleSaveEdit = useCallback(() => {
    if (!editingRow) return;
    const updatedRows = rows.map((r) => {
      if (r._id !== editingRow._id) return r;
      const updated = { ...r };
      for (const col of columns) {
        const val = editValues[col.key] || '';
        if (col.type === 'number') {
          updated[col.key] = val ? Number(val) : null;
        } else if (col.type === 'boolean') {
          updated[col.key] = val === 'true';
        } else {
          updated[col.key] = val;
        }
      }
      return updated;
    });
    setRows(updatedRows);
    setEditingRow(null);
    persistData(columns, updatedRows);
  }, [editingRow, editValues, rows, columns, persistData]);

  if (loading) {
    return <div className='project-app-kanban-block'>Loading…</div>;
  }

  if (!dataTableId || !groupByColumn) {
    return (
      <div className='project-app-kanban-block'>
        Kanban requires a data table and groupByColumn.
      </div>
    );
  }

  if (!enumCol) {
    return (
      <div className='project-app-kanban-block'>
        Column "{groupByColumn}" is not an enum column.
      </div>
    );
  }

  return (
    <div className='project-app-kanban-block'>
      {!embedded && (
        <div className='kanban-toolbar'>
          <button className='kanban-settings-btn' onClick={openSettings}>
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M6.5 1.5h3l.35 1.76a5.5 5.5 0 0 1 1.3.75l1.72-.57 1.5 2.6-1.37 1.19a5.6 5.6 0 0 1 0 1.54l1.37 1.19-1.5 2.6-1.72-.57a5.5 5.5 0 0 1-1.3.75L9.5 14.5h-3l-.35-1.76a5.5 5.5 0 0 1-1.3-.75l-1.72.57-1.5-2.6 1.37-1.19a5.6 5.6 0 0 1 0-1.54L2.63 6.04l1.5-2.6 1.72.57a5.5 5.5 0 0 1 1.3-.75L6.5 1.5Z'
                stroke='currentColor'
                strokeWidth='1.2'
                strokeLinejoin='round'
              />
              <circle
                cx='8'
                cy='8'
                r='2'
                stroke='currentColor'
                strokeWidth='1.2'
              />
            </svg>
            Card fields
          </button>
        </div>
      )}
      <div className='kanban-board'>
        {showUnknown && (
          <div
            className={`kanban-column kanban-column--unknown${dragOverColumn === '__unknown__' ? ' kanban-column--drag-over' : ''}`}
            style={
              {
                '--col-color': columnColors['__unknown__'],
              } as React.CSSProperties
            }
            onDragOver={(e) => handleDragOver(e, '__unknown__')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, '__unknown__')}
          >
            <div className='kanban-column__header'>
              <span className='kanban-column__title'>Unknown</span>
              <span className='kanban-column__count'>
                {groupedRows['__unknown__']?.length || 0}
              </span>
            </div>
            <div className='kanban-column__cards'>
              {groupedRows['__unknown__']?.map((row) => (
                <div
                  key={row._id as string}
                  className='kanban-card'
                  draggable
                  onDragStart={(e) => handleDragStart(e, row._id as string)}
                  onClick={() => openEditModal(row)}
                >
                  {resolvedTitleCol && (
                    <div className='kanban-card__title'>
                      {(row[resolvedTitleCol.key] as string) || ''}
                    </div>
                  )}
                  {resolvedVisibleCols.map((c) => {
                    const val = row[c.key];
                    if (val == null || val === '') return null;
                    return (
                      <div key={c.key} className='kanban-card__field'>
                        <span className='kanban-card__field-label'>
                          {c.label}
                        </span>
                        <span className='kanban-card__field-value'>
                          {String(val)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
        {kanbanColumns.map((colValue) => (
          <div
            key={colValue}
            className={`kanban-column${dragOverColumn === colValue ? ' kanban-column--drag-over' : ''}${dragOverColTarget === colValue ? ' kanban-column--col-drag-over' : ''}`}
            style={
              { '--col-color': columnColors[colValue] } as React.CSSProperties
            }
            onDragOver={(e) => {
              handleDragOver(e, colValue);
              handleColDragOver(e, colValue);
            }}
            onDragLeave={() => {
              handleDragLeave();
              handleColDragLeave();
            }}
            onDrop={(e) => {
              if (dragColumn.current) {
                handleColDrop(e, colValue);
              } else {
                handleDrop(e, colValue);
              }
            }}
          >
            <div
              className='kanban-column__header'
              draggable
              onDragStart={(e) => handleColDragStart(e, colValue)}
              onDragEnd={handleColDragEnd}
            >
              <span className='kanban-column__title'>{colValue}</span>
              <span className='kanban-column__count'>
                {groupedRows[colValue]?.length || 0}
              </span>
            </div>
            <div className='kanban-column__cards'>
              {groupedRows[colValue]?.map((row) => (
                <div
                  key={row._id as string}
                  className='kanban-card'
                  draggable
                  onDragStart={(e) => handleDragStart(e, row._id as string)}
                  onClick={() => openEditModal(row)}
                >
                  {resolvedTitleCol && (
                    <div className='kanban-card__title'>
                      {(row[resolvedTitleCol.key] as string) || ''}
                    </div>
                  )}
                  {resolvedVisibleCols.map((c) => {
                    const val = row[c.key];
                    if (val == null || val === '') return null;
                    return (
                      <div key={c.key} className='kanban-card__field'>
                        <span className='kanban-card__field-label'>
                          {c.label}
                        </span>
                        <span className='kanban-card__field-value'>
                          {String(val)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {saving && <span className='kanban-save-indicator'>Saving…</span>}

      {/* Settings Modal */}
      {showSettings && (
        <div
          className='dt-modal-overlay'
          onClick={() => setShowSettings(false)}
        >
          <div className='dt-modal' onClick={(e) => e.stopPropagation()}>
            <div className='dt-modal-header'>
              <h2>Card Display</h2>
              <button
                className='dt-modal-cancel'
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
            </div>
            <div className='dt-modal-body'>
              <div className='dt-field'>
                <label>Title field</label>
                <div className='kanban-settings-list'>
                  {cardColumns.map((c) => (
                    <label key={c.key} className='kanban-settings-option'>
                      <input
                        type='radio'
                        name='kanban-title-field'
                        checked={draftTitleField === c.key}
                        onChange={() => setDraftTitleField(c.key)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className='dt-field'>
                <label>Visible fields</label>
                <div className='kanban-settings-list'>
                  {cardColumns
                    .filter((c) => c.key !== draftTitleField)
                    .map((c) => (
                      <label key={c.key} className='kanban-settings-option'>
                        <input
                          type='checkbox'
                          checked={draftVisibleFields.includes(c.key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDraftVisibleFields((prev) => [...prev, c.key]);
                            } else {
                              setDraftVisibleFields((prev) =>
                                prev.filter((k) => k !== c.key),
                              );
                            }
                          }}
                        />
                        {c.label}
                      </label>
                    ))}
                </div>
              </div>
            </div>
            <div className='dt-modal-footer'>
              <button className='dt-btn-primary' onClick={handleSaveSettings}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Card Modal */}
      {editingRow && (
        <div className='dt-modal-overlay' onClick={() => setEditingRow(null)}>
          <div className='dt-modal' onClick={(e) => e.stopPropagation()}>
            <div className='dt-modal-header'>
              <h2>Edit Card</h2>
              <button
                className='dt-modal-cancel'
                onClick={() => setEditingRow(null)}
              >
                Cancel
              </button>
            </div>
            <div className='dt-modal-body'>
              {columns.map((col) => (
                <div className='dt-field' key={col.key}>
                  <label>{col.label}</label>
                  {col.type === 'boolean' ? (
                    <div
                      className={`dt-toggle${editValues[col.key] === 'true' ? ' active' : ''}`}
                      onClick={() =>
                        setEditValues((prev) => ({
                          ...prev,
                          [col.key]:
                            prev[col.key] === 'true' ? 'false' : 'true',
                        }))
                      }
                    >
                      <div className='dt-toggle-thumb' />
                    </div>
                  ) : col.type === 'enum' && col.options ? (
                    <select
                      value={editValues[col.key] || ''}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }))
                      }
                    >
                      <option value=''>Select…</option>
                      {col.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={
                        col.type === 'number'
                          ? 'number'
                          : col.type === 'date'
                            ? 'date'
                            : col.type === 'email'
                              ? 'email'
                              : col.type === 'url'
                                ? 'url'
                                : 'text'
                      }
                      value={editValues[col.key] || ''}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }))
                      }
                      placeholder={col.label}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className='dt-modal-footer'>
              <button className='dt-btn-primary' onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAppKanbanBlock;
